import { useMemo } from 'react';
import {
  Badge,
  Body1Strong,
  Card,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  createTableColumn,
  useTableFeatures,
} from '@fluentui/react-components';
import ArchiveTableAction from '@shared/components/tables/ArchiveTableAction';

import { ControlledTooltip, DateLabel, SmartTableCell } from '@shared/components';
import { usePersistedColumnSizing } from '@shared/hooks';
import { useItemTableStyles } from '@modules/items';

import { TLiterature } from '../types';
import {
  getAssessmentForProduct,
  getDecisionBadgeColor,
  getDecisionLabel,
  getLiteratureDisplayContent,
  getTypeLabel,
} from '../helpers';
import { LITERATURES_TABLE_ID } from '../constants';

// Matches the canonical list-table pattern used elsewhere in the app:
// Card + useItemTableStyles + SmartTableCell on the name column with the
// three-dots action menu passed as `after`. Column widths are resizable and
// persisted via usePersistedColumnSizing (same as AccessLogsTable).
type TProps = {
  items: TLiterature[];
  productId?: string;
  canEdit: boolean;
  onArchive: (item: TLiterature) => void;
};

type TColumnId = 'title' | 'type' | 'year' | 'authors' | 'assessment' | 'modified';

// Mirrors SmartTableCell's threshold pattern: tooltip fires only when the
// joined authors string exceeds this many characters, so short rows don't
// get a redundant hover tooltip. Title column uses 70 via SmartTableCell.
const AUTHORS_TOOLTIP_THRESHOLD = 30;

// autoFitColumns stays false - true would re-run width calculation on every render and overwrite user drag-resize.
const columnSizingParams = {
  autoFitColumns: false,
  columnSizingOptions: {
    title: { defaultWidth: 320, minWidth: 160 },
    type: { defaultWidth: 120, minWidth: 90 },
    year: { defaultWidth: 60, minWidth: 50 },
    authors: { defaultWidth: 180, minWidth: 100 },
    assessment: { defaultWidth: 100, minWidth: 90 },
    modified: { defaultWidth: 100, minWidth: 90 },
  },
};

const LiteratureTable = ({ items, productId, canEdit, onArchive }: TProps) => {
  const classes = useItemTableStyles();

  const columnIds: TColumnId[] = useMemo(
    () =>
      productId
        ? ['title', 'type', 'year', 'authors', 'assessment', 'modified']
        : ['title', 'type', 'year', 'authors', 'modified'],
    [productId]
  );

  const columns = useMemo(() => columnIds.map((columnId) => createTableColumn<TLiterature>({ columnId })), [columnIds]);

  const { columnSizing_unstable, getRows } = useTableFeatures({ columns, items, getRowId: (item) => item._id }, [
    usePersistedColumnSizing(LITERATURES_TABLE_ID, columnSizingParams),
  ]);

  const rows = getRows();

  return (
    <Card className={classes.card}>
      <Table className={classes.table} {...columnSizing_unstable.getTableProps()}>
        <TableHeader>
          <TableRow>
            <TableHeaderCell {...columnSizing_unstable.getTableHeaderCellProps('title')}>
              <Body1Strong>Title</Body1Strong>
            </TableHeaderCell>
            <TableHeaderCell {...columnSizing_unstable.getTableHeaderCellProps('type')}>
              <Body1Strong>Type</Body1Strong>
            </TableHeaderCell>
            <TableHeaderCell {...columnSizing_unstable.getTableHeaderCellProps('year')}>
              <Body1Strong>Year</Body1Strong>
            </TableHeaderCell>
            <TableHeaderCell {...columnSizing_unstable.getTableHeaderCellProps('authors')}>
              <Body1Strong>Authors</Body1Strong>
            </TableHeaderCell>
            {productId && (
              <TableHeaderCell {...columnSizing_unstable.getTableHeaderCellProps('assessment')}>
                <Body1Strong>Assessment</Body1Strong>
              </TableHeaderCell>
            )}
            <TableHeaderCell {...columnSizing_unstable.getTableHeaderCellProps('modified')}>
              <Body1Strong>Modified</Body1Strong>
            </TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody className={classes.tableBody}>
          {rows.map(({ item }) => {
            const content = getLiteratureDisplayContent(item);
            const assessment = getAssessmentForProduct(item, productId);
            const authors = content?.authors?.join(', ') ?? '';

            return (
              <TableRow key={item._id}>
                <SmartTableCell
                  cellProps={columnSizing_unstable.getTableCellProps('title')}
                  variant={{
                    kind: 'link',
                    href: `/literature/${item._id}`,
                    label: content?.title ?? '(untitled)',
                  }}
                  tooltip={{ threshold: 70 }}
                  after={canEdit ? <ArchiveTableAction label="Archive" onArchive={() => onArchive(item)} /> : null}
                />
                <TableCell {...columnSizing_unstable.getTableCellProps('type')}>
                  {content ? getTypeLabel(content.type) : '–'}
                </TableCell>
                <TableCell {...columnSizing_unstable.getTableCellProps('year')}>
                  {content?.publicationYear ?? '–'}
                </TableCell>
                <TableCell {...columnSizing_unstable.getTableCellProps('authors')}>
                  <ControlledTooltip
                    enable={authors.length > AUTHORS_TOOLTIP_THRESHOLD}
                    content={authors}
                    relationship="description"
                    positioning="above-start"
                  >
                    <span className="inline-block max-w-full truncate align-bottom">{authors || '–'}</span>
                  </ControlledTooltip>
                </TableCell>
                {productId && (
                  <TableCell {...columnSizing_unstable.getTableCellProps('assessment')}>
                    {assessment && (
                      <Badge appearance="tint" color={getDecisionBadgeColor(assessment.decision)}>
                        {getDecisionLabel(assessment.decision)}
                      </Badge>
                    )}
                  </TableCell>
                )}
                <TableCell {...columnSizing_unstable.getTableCellProps('modified')}>
                  <DateLabel short>{item.stats.modifiedDate}</DateLabel>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Card>
  );
};

export default LiteratureTable;
